import { RoutePath } from 'features/routing/constants';
import { LOCAL_STORAGE_AUTH_RETURN_URL } from '../constants';
import { isNoSessionRoute, matchesRoute } from './appRenderRule';

// oidc-client-ts sends the OIDC state to Identity as `<id>;<url_state>` and
// decodes the returned value once more before splitting it, so `url_state` is
// sent percent-encoded. Identity returns it on every callback, also when this
// tab no longer holds the stored state.
const URL_STATE_DELIMITER = ';';
// Counted on the encoded value; oidc-client-ts encodes it once more in the
// authorize URL, which Identity limits in length.
const MAX_URL_STATE_LENGTH = 1000;

export type SigninTarget =
  { kind: 'valid'; path: string } | { kind: 'rejected' | 'none' };

const isSigninTarget = (path: string): boolean => {
  if (
    encodeURIComponent(path).length > MAX_URL_STATE_LENGTH ||
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.includes('\\')
  ) {
    return false;
  }

  let url: URL;
  try {
    url = new URL(path, window.location.origin);
  } catch {
    return false;
  }

  return (
    url.origin === window.location.origin && !isNoSessionRoute(url.pathname)
  );
};

/** The `url_state` that carries `path` through a login redirect, when it may be returned to. */
export const toSigninUrlState = (path: string): string | undefined =>
  isSigninTarget(path) ? encodeURIComponent(path) : undefined;

/** `signinRedirect` arguments that return the sign-in to the current page. */
export const signinRedirectTarget = (): {
  state: { returnUrl: string };
  url_state?: string;
} => {
  const returnUrl = window.location.pathname + window.location.search;
  const urlState = toSigninUrlState(returnUrl);

  return { state: { returnUrl }, ...(urlState && { url_state: urlState }) };
};

/** The page the sign-in started from, read from the callback's `state` parameter. */
export const signinTargetFromCallback = (search: string): SigninTarget => {
  const state = new URLSearchParams(search).get('state') ?? '';
  const delimiter = state.indexOf(URL_STATE_DELIMITER);
  if (delimiter === -1) {
    return { kind: 'none' };
  }

  let path: string;
  try {
    path = decodeURIComponent(state.slice(delimiter + 1));
  } catch {
    return { kind: 'rejected' };
  }

  return isSigninTarget(path) ? { kind: 'valid', path } : { kind: 'rejected' };
};

const savedReturnUrl = (): string | null => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_AUTH_RETURN_URL);
  } catch {
    return null;
  }
};

/**
 * The page "Try again" on the sign-in error page returns to: the current page;
 * on the callback the page its `state` carries, or, once a completed Identity
 * callback has removed its query, the `authReturnUrl` it saved; otherwise `/`.
 */
export const signinRetryPath = (): string => {
  const { pathname, search } = window.location;
  if (pathname.startsWith('//')) {
    return RoutePath.Root;
  }
  if (!isNoSessionRoute(pathname)) {
    return pathname + search;
  }
  if (!matchesRoute(RoutePath.AuthCallback, pathname)) {
    return RoutePath.Root;
  }
  if (search !== '') {
    const target = signinTargetFromCallback(search);
    return target.kind === 'valid' ? target.path : RoutePath.Root;
  }
  const saved = savedReturnUrl();
  return saved !== null && isSigninTarget(saved) ? saved : RoutePath.Root;
};
